import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { ACCOUNT_FOLDERS_TAG_TYPE } from './constants';

export interface AccountFolderFilterSet {
  id: number;
  label: string;
  type: string;
  scope: 'PERSONAL' | 'COMPANY';
}

export interface AccountFolderProviderCohort {
  id: number;
  owner: {
    id: number;
    first_name: string;
    last_name: string;
    username: string;
  };
  label: string;
  scope: 'PERSONAL' | 'COMPANY';
  status: string;
}

export interface AccountFolderAdvancedSearch {
  id: number;
  label: string;
  scope: 'PERSONAL' | 'COMPANY';
  status: string;
  created_by: {
    id: number;
    first_name: string;
    last_name: string;
    username: string;
  };
}

export interface AccountFolder {
  id: number;
  label: string;
  scope: 'PERSONAL' | 'COMPANY';
  filter_sets: AccountFolderFilterSet[];
  provider_cohorts: AccountFolderProviderCohort[];
  advanced_searches?: AccountFolderAdvancedSearch[];
}

export interface AccountFoldersResponse {
  results: AccountFolder[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export interface AccountFolderRequest {
  label: string;
  scope: 'COMPANY' | 'PERSONAL';
}

export const accountFoldersApi = createApi({
  reducerPath: 'accountFoldersApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [ACCOUNT_FOLDERS_TAG_TYPE],
  endpoints: (builder) => ({
    getAccountFolders: builder.query<AccountFoldersResponse, void>({
      query: () => ({
        url: 'v1/accounts/filter-set-folders/',
        method: 'GET',
      }),
      providesTags: [{ type: ACCOUNT_FOLDERS_TAG_TYPE, id: 'LIST' }],
    }),

    getAccountFolderById: builder.query<AccountFolder, string>({
      query: (id) => ({
        url: `v1/accounts/filter-set-folders/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [
        { type: ACCOUNT_FOLDERS_TAG_TYPE, id },
      ],
    }),

    createAccountFolder: builder.mutation<AccountFolder, AccountFolderRequest>({
      query: (data) => ({
        url: 'v1/accounts/filter-set-folders/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: ACCOUNT_FOLDERS_TAG_TYPE, id: 'LIST' }],
    }),

    updateAccountFolder: builder.mutation<
      AccountFolder,
      { id: string; data: AccountFolderRequest }
    >({
      query: ({ id, data }) => ({
        url: `v1/accounts/filter-set-folders/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: ACCOUNT_FOLDERS_TAG_TYPE, id: 'LIST' },
        { type: ACCOUNT_FOLDERS_TAG_TYPE, id },
      ],
    }),

    deleteAccountFolder: builder.mutation<void, string>({
      query: (id) => ({
        url: `v1/accounts/filter-set-folders/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: ACCOUNT_FOLDERS_TAG_TYPE, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAccountFoldersQuery,
  useGetAccountFolderByIdQuery,
  useCreateAccountFolderMutation,
  useUpdateAccountFolderMutation,
  useDeleteAccountFolderMutation,
} = accountFoldersApi;
